
import axios from 'axios';
import Vue, { CreateElement } from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

const TitleListGridREST = () =>
	import(/* webpackChunkName: "title-list-grid" */ '@/components/list/TitleListGridREST.vue');
const TitleListCardREST = () =>
	import(/* webpackChunkName: "title-list-card" */ '@/components/list/TitleListCardREST.vue');

import BasicButton from '@/ui-components/BasicButton.vue';
import ChipButton from '@/ui-components/ChipButton.vue';

import CountrySwitcherModal from '@/components/buybox/country-switcher/CountrySwitcherModal.vue';

import PlotRecommenderContentTypeFilter from './PlotRecommenderContentTypeFilter.vue';
import PlotRecommenderMonetizationTypeFilter from './PlotRecommenderMonetizationType.vue';
import PlotRecommenderPriceFilter from './PlotRecommenderPriceFilter.vue';
import PlotRecommenderProviderFilter from './PlotRecommenderProviderFilter.vue';
import PlotRecommenderRatingFilter from './PlotRecommenderRatingFilter.vue';
import RecommenderTitleCard from './RecommenderTitleCard.vue';

import { TitleOrder } from '@/enums/title-order';
import { ModalHelper } from '@/helpers/modal-helper';
import { decorateFullPath } from '@/helpers/title-helper';
import { PopularTitle } from '@/interfaces/titles';

import { getAxios } from '@/helpers/axios-helper';
import { PopularResponse } from '@/interfaces/responses/popular';
import { FilterCollection } from '@/stores/filter.store';
import lpRecommenderStore, { PlotRecommenderState } from './plot-recommender.store';

const Language = namespace('language');
const LpPlotRecommender = namespace('LpPlotRecommender');

@Component({
	name: 'PlotRecommender',
	components: {
		BasicButton,
		TitleListGridREST,
		TitleListCardREST,
		RecommenderTitleCard,
		PlotRecommenderProviderFilter,
		PlotRecommenderContentTypeFilter,
		ChipButton,
	},
})
export default class PlotRecommender extends Vue {
	@Language.Getter language: string;
	@Language.Getter locale: string;
	@Language.State webLocale: string;
	@Language.Getter countryEmoji: string;

	@LpPlotRecommender.State isFetching: boolean;
	@LpPlotRecommender.State filters: PlotRecommenderState['filters'];
	@LpPlotRecommender.Getter isAnyFilterActive: Function;
	@LpPlotRecommender.Action resetFilters: Function;
	@LpPlotRecommender.Mutation SET_FILTER: Function;
	@LpPlotRecommender.Mutation SET_FETCHING: Function;

	synopsis = '';
	boosting = 'none';
	titles: PopularTitle[] = [];
	ghostwriteIndex = 0;
	recommendationList: { jw_entity_id: string; score: number }[] = [];
	synopsisPresets = [
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_1'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_2'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_3'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_4'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_5'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_6'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_7'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_8'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_9'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_10'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_11'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_12'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_13'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_14'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_15'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_16'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_17'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_18'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_19'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_20'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_21'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_22'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_23'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_24'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_25'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_26'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_27'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_28'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_29'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_30'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_31'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_32'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_33'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_34'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_35'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_36'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_37'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_38'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_39'),
		this.$t('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_40'),
	];

	// the typewriter the user can interact with can be quite large and bring the recommendations below the fold depending on the viewport.
	// if there was recommendation changes, we want to display that there was a change by displaying a glowing box shadow at the bottom to signal scrolling.
	isShadowScrollerShown = false;
	isAdditionalFiltersShown = false;
	isGhostWriting = false;
	timeoutId: NodeJS.Timeout;

	beforeCreate() {
		this.$store.registerModule('LpPlotRecommender', lpRecommenderStore);
	}

	async mounted() {
		this.synopsisPresets.sort(() => 0.5 - Math.random());

		window.addEventListener('scroll', this.onScroll);
		this.$once('hook:destroyed', () => window.removeEventListener('scroll', this.onScroll));
	}

	@Watch('filters', { deep: true })
	async onFilterChanged() {
		if (this.recommendationList.length) {
			await this.fetchTitles();
		}
	}

	get matchingScores() {
		return Object.fromEntries(
			this.recommendationList.map(({ jw_entity_id, score }) => [jw_entity_id, parseInt(score * 100 + '')])
		);
	}

	get ASSETS_DIR() {
		return `/${ASSETS_DIR}`;
	}

	get isAnyAdditionalFilterActive() {
		return this.isAnyFilterActive(['min_price', 'max_price', 'scoring_filter_types', 'monetization_types']);
	}

	async onChange(delay = 1500) {
		this.isShadowScrollerShown = false;

		if (this.timeoutId && this.synopsis) clearTimeout(this.timeoutId);

		if (this.synopsis) {
			this.timeoutId = setTimeout(async () => {
				this.isGhostWriting = false;
				await this.fetchRecommendations();

				// check if the recommendations are in viewport, otherwise display pulsing shadow box
				if (!this.isElementInViewport(this.$refs.recommendations as Element)) {
					this.isShadowScrollerShown = true;
				}
			}, delay);
		}
	}

	onScroll() {
		if (this.isShadowScrollerShown && this.isElementInViewport(this.$refs.recommendations as Element)) {
			this.isShadowScrollerShown = false;
		}
	}

	async ghostwrite() {
		const STROKE_TIME = 25;

		this.isShadowScrollerShown = false;
		this.isGhostWriting = true;

		this.synopsis = '';
		let letterIndex = 0;
		const targetSynopsis = <string>this.synopsisPresets[this.ghostwriteIndex % this.synopsisPresets.length];

		const interval = setInterval(async () => {
			this.synopsis += targetSynopsis[letterIndex];

			if (letterIndex + 1 >= targetSynopsis.length) clearInterval(interval);

			letterIndex++;
		}, STROKE_TIME);

		const ghostWriterTimoutId = setTimeout(async () => {
			await this.onChange();
			clearTimeout(ghostWriterTimoutId);
		}, STROKE_TIME * targetSynopsis.length);

		this.ghostwriteIndex++;
	}

	async fetchRecommendations() {
		this.SET_FETCHING(true);
		const response = await axios.post<{
			recommendations: { recommendation_list: { jw_entity_id: string; score: number }[] };
		}>(`${(JW_CONFIG as any).PLOT_RECOMMENDER_URL}/plot-recommender/textualrecommendations`, {
			synopsis: this.synopsis,
			output_limit: 20,
			locale: this.language ?? 'en',
		});

		this.recommendationList = response.data.recommendations.recommendation_list;
		await this.fetchTitles();
	}

	async fetchTitles() {
		this.SET_FETCHING(true);
		const recommendations = this.recommendationList.map(item => item.jw_entity_id);
		const titleResponse = await this.getTitles(
			this.locale,
			recommendations,
			{
				sort_by: TitleOrder.LAST_ADDED,
				fields: [
					'id',
					'jw_entity_id',
					'title',
					'full_path',
					'full_paths',
					'poster',
					'object_type',
					'scoring',
					'offers',
					'short_description',
					'clips',
				],
				monetization_types: ['free', 'ads', 'flatrate', 'buy', 'rent'],
				...this.filters,
			} as any,
			1,
			20 // recommendations.length
		);
		this.titles = titleResponse.items;
		this.SET_FETCHING(false);
	}

	/**
	 * retrieve Titles and filter them, or get specific titles with the entityId array of strings
	 *
	 * @param {string} locale
	 * @param {string[]} entityIds
	 * @param {Partial<FilterCollection>} filters
	 * @param {number} page
	 * @param {number} page_size
	 */
	async getTitles(
		locale: string,
		entityIds: string[], // in form of ts2, tm392, etc
		filters?: Partial<FilterCollection>,
		page = 1,
		page_size = 10
	): Promise<PopularResponse> {
		const url = `${JW_CONFIG.API}/titles/${locale}/popular`;
		const params = {
			body: {
				jw_entity_ids: entityIds,
				...filters,
				page_size,
				page,
			},
			secret: 'G1veM3B4ckMyL3gacyR0utes',
		};
		const response = await getAxios().get(url, { params });
		return decorateFullPath(response);
	}

	async openPopover(event: Event) {
		this.isAdditionalFiltersShown = true;
		const { popoverController } = await import('@/ionic.loader');

		const modal = await popoverController.create({
			cssClass: 'popover__filter',
			component: {
				render: (h: CreateElement) => {
					const children = [
						PlotRecommenderRatingFilter,
						PlotRecommenderPriceFilter,
						PlotRecommenderMonetizationTypeFilter,
					].map((item: any) => {
						return h(item);
					});
					return h('div', children);
				},
			},
			componentProps: { parent: this },
			event,
			mode: 'ios',
		} as any);
		modal.present();

		await modal.onWillDismiss();
		this.isAdditionalFiltersShown = false;
	}

	private isElementInViewport(el: Element, bufferPixel = 100) {
		const rect = el.getBoundingClientRect();
		return rect.top + bufferPixel < (window.innerHeight || document.documentElement.clientHeight);
	}

	openCountrySwitcherModal() {
		ModalHelper.openModal(CountrySwitcherModal, { closable: true });
	}
}
